import { VERIFICATION_CHECKLIST_ITEMS, WORK_ORDER_VERIFICATION_UPLOAD_ENTITY_TYPE } from '@chukua/common';
import { Alert, Button, Input } from 'antd';
import { useState } from 'react';
import { FileUpload } from '../../../../../shared/features/uploads/components/FileUpload';
import { blankVerificationForm, previewVerification, verificationBlocker, type VerificationFormValues } from '../policies/verification-form.policy';
import { VerificationCheckRow } from './VerificationCheckRow';

const OUTCOME_NOTE = {
  incomplete: 'Answer all four checks to see what submitting does.',
  close: 'Every check passed: submitting verifies and closes the order.',
  send_back: 'A check failed: submitting sends the order back to in progress for rework.',
} as const;

interface VerificationFormProps {
  workOrderId: string;
  error: string | null;
  submitting: boolean;
  onSubmit: (values: VerificationFormValues) => Promise<boolean>;
}

/** The outcome is not chosen: it follows from the four answers, and the form says which way it will go before it is submitted. */
export function VerificationForm({ workOrderId, error, submitting, onSubmit }: VerificationFormProps) {
  const [values, setValues] = useState<VerificationFormValues>(blankVerificationForm);
  const preview = previewVerification(values);
  const blocker = verificationBlocker(values);

  async function submit() {
    if (await onSubmit(values)) setValues(blankVerificationForm());
  }

  return (
    <section aria-label="Record verification" className="flex flex-col gap-4">
      <h2 className="m-0 text-base font-semibold">Record verification</h2>
      {error && <Alert type="error" title={error} showIcon />}
      {VERIFICATION_CHECKLIST_ITEMS.map((item) => (
        <VerificationCheckRow
          key={item}
          item={item}
          answer={values[item]}
          disabled={submitting}
          onChange={(answer) => setValues({ ...values, [item]: answer })}
        />
      ))}
      {preview === 'send_back' && (
        <div className="flex flex-col gap-1">
          <label htmlFor="verification-reason" className="font-medium">
            Why did the work fail?
          </label>
          <Input.TextArea
            id="verification-reason"
            rows={3}
            maxLength={500}
            value={values.reason}
            onChange={(event) => setValues({ ...values, reason: event.target.value })}
          />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <span className="font-medium">Photos{preview === 'close' ? ' (at least one)' : ''}</span>
        <FileUpload
          entityType={WORK_ORDER_VERIFICATION_UPLOAD_ENTITY_TYPE}
          entityId={workOrderId}
          onUploaded={(upload) => setValues((current) => ({ ...current, photoUploadIds: [...current.photoUploadIds, upload.id] }))}
        />
        {values.photoUploadIds.length > 0 && <span className="text-sm text-muted">{values.photoUploadIds.length} photo(s) added</span>}
      </div>
      <Alert type={preview === 'send_back' ? 'warning' : 'info'} title={OUTCOME_NOTE[preview]} showIcon />
      <div className="flex items-center gap-3">
        <Button type="primary" disabled={blocker !== null} loading={submitting} onClick={submit}>
          Submit verification
        </Button>
        {blocker && <span className="text-sm text-muted">{blocker}</span>}
      </div>
    </section>
  );
}
